import {computed, ref} from "vue";
import dayjs from "dayjs";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import useLatestFetch from "@/core/composables/useLatestFetch";
import {mutate} from "swrv";
import {teamPlanningApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {HourMovementUpdateRequest} from "@/core/http/resources/TeamPlanningResource";
import {TeamHourBalance, TeamHourBalances, TeamHourMovement} from "@/core/interfaces/TeamPlanning";

const EMPTY_BALANCES: TeamHourBalances = {
    members: [],
    total: {overtime_minutes: 0, recovery_minutes: 0, balance_minutes: 0},
};

/** Depth of history the counter dialog reads, in months. */
export const HISTORY_MONTHS = 12;

/**
 * The team worked-hours counter: cached balances feeding the planning
 * badges, and the movement history behind the counter dialog.
 */
const useHourCounter = () => {
    const organizationId = useOrganizationId();

    const balancesKey = computed(() =>
        organizationId.value ? cacheKey(`organizations/${organizationId.value}/team-hour-balances`) : null
    );

    const {data: balancesData, error: balancesError} = useSwr(
        () => balancesKey.value,
        () => organizationId.value ? teamPlanningApi().hourBalances(organizationId.value) : Promise.resolve(EMPTY_BALANCES)
    );

    const balances = computed<TeamHourBalances>(() => balancesData.value ?? EMPTY_BALANCES);

    /**
     * A rejected fetch leaves swrv without data, which is indistinguishable
     * from a pharmacy that never used the counter: without this the screens
     * would present a failure as a legitimate zero.
     */
    const balancesFailed = computed<boolean>(() => !!balancesError.value && balancesData.value === undefined);

    /**
     * Re-read the synthesis and write it straight into the swrv cache.
     * Never throws: callers refresh after their own write already succeeded,
     * and a failed refresh must not read as a failed write.
     */
    const refreshBalances = async () => {
        if (!organizationId.value || !balancesKey.value) {
            return;
        }

        try {
            mutate(balancesKey.value, await teamPlanningApi().hourBalances(organizationId.value));
        } catch {
            // Keep the cached synthesis.
        }
    };

    const movements = ref<TeamHourMovement[]>([]);

    /** Scope the rows belong to: a member id, null for the whole team. */
    const loadedScope = ref<number | null | undefined>(undefined);
    let requestedScope: number | null = null;

    const {loading: movementsLoading, failed: movementsFailed, run: runMovements} = useLatestFetch();

    /**
     * The history is bounded and optionally scoped to one member: mirroring
     * every dated override, it grows without end, and the whole team's is
     * rarely what the reader asked for.
     */
    const fetchMovements = async (memberId?: number | null) => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId) {
            return;
        }

        const scope = memberId ?? null;
        requestedScope = scope;

        // Rows of another scope must not sit under the new heading, but rows of
        // the same one stay until the fresh list lands — reloading after an
        // edit should not blank the panel.
        if (loadedScope.value !== scope) {
            movements.value = [];
            loadedScope.value = undefined;
        }

        await runMovements(async isLatest => {
            const next = await teamPlanningApi().hourMovements(currentOrganizationId, {
                from: dayjs().subtract(HISTORY_MONTHS, 'month').format('YYYY-MM-DD'),
                ...(scope ? {user_access_point_id: scope} : {}),
            });

            if (isLatest()) {
                movements.value = next;
                loadedScope.value = scope;
            }
        });
    };

    const balanceByMember = computed<Map<number, TeamHourBalance>>(() =>
        new Map(balances.value.members.map(balance => [balance.user_access_point_id, balance]))
    );

    /**
     * Write then reload, rather than patching the list in place: a local edit
     * racing a reload of another scope used to be applied to an already-cleared
     * array and lost, leaving an empty panel with neither loader nor error.
     * The reload is the latest run by construction, so it always wins.
     */
    const reload = () => fetchMovements(requestedScope);

    const updateMovement = async (movementId: number, request: HourMovementUpdateRequest) => {
        await teamPlanningApi().updateHourMovement(movementId, request);
        await Promise.all([reload(), refreshBalances()]);
    };

    const removeMovement = async (movementId: number) => {
        await teamPlanningApi().deleteHourMovement(movementId);
        await Promise.all([reload(), refreshBalances()]);
    };

    return {
        balances,
        balancesFailed,
        balanceByMember,
        refreshBalances,
        movements,
        movementsLoading,
        movementsFailed,
        fetchMovements,
        updateMovement,
        removeMovement,
    };
};

export default useHourCounter;
